import { createStyles } from 'antd-style';

import {
  ThemeColors,
  ThemeFont,
  ThemeSpaces,
  hexToRgbaWithAlpha,
} from '../theme';
import { responsiveMdVsLg } from '../styles/responsiveMixins';
import { FA_SELECTOR } from '../styles/variables';

const centerOfScreenForm = (token, maxHeight) => ({
  [`@media (min-width: ${token.screenXLMin}px)`]: {
    minHeight: maxHeight,
  },
  [`@media (min-width: ${token.screenLGMin}px) and (max-width: ${token.screenLGMax}px)`]:
    {
      minHeight: Math.min(420, maxHeight),
    },
  [`@media (min-width: ${token.screenMDMin}px) and (max-width: ${token.screenMDMax}px)`]:
    {
      minHeight: Math.min(390, maxHeight),
    },

  'h4.ant-typography': {
    color: ThemeColors.COLOR_GREY2,
    textAlign: 'center',
    fontWeight: 400,
    lineHeight: '21px',
  },

  a: {
    textDecoration: 'underline',
  },
});

export const useAuthEnvelopeStyles = createStyles(({ token }) => ({
  authEnvelope: {
    backgroundColor: ThemeColors.COLOR_WHITE2,
    boxShadow: `2px 2px 25px ${hexToRgbaWithAlpha(ThemeColors.COLOR_GREY_BLUISH, 0.35)}`,
    border: `1px solid ${token.colorWhite}`,
    borderRadius: token.borderRadiusMD,

    [`@media (min-width: ${token.screenXLMin}px)`]: {
      width: 460,
      padding: `${ThemeSpaces.SPACE_NORM_30}px ${ThemeSpaces.SPACE_XL}px`,
    },
    [`@media (min-width: ${token.screenLGMin}px) and (max-width: ${token.screenLGMax}px)`]:
      {
        width: 420,
        padding: `${ThemeSpaces.SPACE_NORM_20}px ${ThemeSpaces.SPACE_NORM_30}px`,
      },
    [`@media (min-width: ${token.screenMDMin}px) and (max-width: ${token.screenMDMax}px)`]:
      {
        width: 320,
        padding: `${ThemeSpaces.SPACE_NORM_20}px`,
      },
    [`@media (max-width: ${token.screenSMMax}px)`]: {
      width: '100%',
      alignSelf: 'stretch',
      padding: `${ThemeSpaces.SPACE_NORM_20}px 0`,
      backgroundColor: 'transparent',
      boxShadow: 'none',
      borderWidth: 0,
    },

    '.ant-btn.ant-btn-primary': {
      width: '100%',
      height: token.controlHeightLG,
      fontSize: token.fontSizeHeading3,
      letterSpacing: '0.5px',
      fontWeight: 'normal',
      boxShadow: `0 4px 20px ${hexToRgbaWithAlpha(ThemeColors.COLOR_GREY_BLUISH, 0.85)}`,
    },

    '.ant-checkbox-wrapper': {
      color: ThemeColors.COLOR_GREY3,
      fontSize: token.fontSizeLG,
    },
  },
  logo: {
    margin: '0 auto',
  },
  loginFormLogo: {
    ...responsiveMdVsLg(
      { marginBottom: ThemeSpaces.SPACE_NORM_25 },
      { marginBottom: ThemeSpaces.SPACE_XL }
    ),
  },
  centerOfScreenFormLogo: {
    marginBottom: ThemeSpaces.SPACE_SM,
  },
  title: {
    textAlign: 'center',
    color: ThemeColors.COLOR_GREY3,
    fontSize: token.fontSizeHeading1,
    fontWeight: 300,
    lineHeight: '42px',
    marginBottom: ThemeSpaces.SPACE_NORM_20,
  },
  subtitle: {
    textAlign: 'center',
    color: ThemeColors.COLOR_GREY3,
    fontSize: ThemeFont.FONT_SIZE_HEADING_3_5,
    fontWeight: 400,
    lineHeight: '24px',
    marginBottom: ThemeSpaces.SPACE_NORM,
  },
  infoText: {
    textAlign: 'center',
    color: ThemeColors.COLOR_GREY3,
    fontSize: token.fontSizeHeading4,
    fontWeight: 400,
    lineHeight: '21px',
    marginBottom: ThemeSpaces.SPACE_XL,
  },
  links: {
    color: token.colorPrimary,
    fontWeight: 'bold',
    fontSize: token.fontSizeLG,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loginForm: {
    paddingTop: ThemeSpaces.SPACE_NORM_20,
  },
  loginFormItems: {
    '> :nth-child(1)': {
      marginBottom: ThemeSpaces.SPACE_NORM_25,
    },
    '> :nth-child(2)': {
      marginBottom: ThemeSpaces.SPACE_NORM,
    },
    '> :nth-child(3)': {
      ...responsiveMdVsLg(
        { marginBottom: ThemeSpaces.SPACE_NORM_20 },
        { marginBottom: ThemeSpaces.SPACE_NORM_30 }
      ),
    },
    '> :nth-child(4)': {
      marginBottom: ThemeSpaces.SPACE_NORM_15,
    },
  },
  passwordResetInitForm: {
    ...centerOfScreenForm(token, 430),

    '> form > *': {
      marginBottom: ThemeSpaces.SPACE_NORM_20,
    },
  },
  passwordResetEmailSentMessage: {
    ...centerOfScreenForm(token, 260),
  },
  newPasswordForm: {
    ...centerOfScreenForm(token, 450),

    '> form > .ant-form-item': {
      marginBottom: ThemeSpaces.SPACE_LG,
    },
    '> form > button': {
      marginBottom: ThemeSpaces.SPACE_NORM_15,
    },
  },
  newPasswordFormTitle: {
    marginBottom: ThemeSpaces.SPACE_NORM_15,
  },
  passwordResetInitFormTitle: {
    marginBottom: ThemeSpaces.SPACE_NORM_20,
  },
  passwordResetEmailSentMessageTitle: {
    marginBottom: ThemeSpaces.SPACE_LG,
  },
  validationSubruleResults: {
    marginBottom: ThemeSpaces.SPACE_NORM,
  },
  validationSubruleResult: {
    color: ThemeColors.COLOR_GREY2,
    fontSize: token.fontSizeLG,

    '.ant-col:first-child': {
      width: 24,
    },
    '.ant-col:last-child': {
      flex: 1,
      minWidth: 0,
      minHeight: 0,
    },
    [FA_SELECTOR]: {
      fontSize: 18,
      '&.success': {
        color: ThemeColors.COLOR_GREEN,
      },
      '&.error': {
        color: token.colorPrimary,
      },
    },
  },
}));
